import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { useSchool } from '../contexts/SchoolContext';
import { supabase } from '../lib/supabase';
import { statusColor, statusLabel } from '../lib/applications';
import { motion } from 'framer-motion';
import {
  FaClipboardList, FaEnvelope, FaCheckCircle, FaClock, FaSearch, FaFileAlt, FaEye
} from 'react-icons/fa';
import './AdminDashboard.css';

// "What needs my attention?" Counts come from one database call (dashboard_stats); recent applications are read
// with explicit columns (medical answers are never needed here).
const ENQUIRY_TYPE = { general: 'enquiry', admissions: 'admissions enquiry', visit_request: 'visit request' };
const ADMISSION_STATE = {
  open: { label: 'Admissions open', className: 'open' },
  upcoming: { label: 'Admissions opening soon', className: 'upcoming' },
  closed: { label: 'Admissions closed', className: 'closed' }
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const { gradeLabel } = useSchool();
  const [stats, setStats] = useState(null);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const openFilteredApplications = (filter) => {
    const params = new URLSearchParams(filter);
    navigate(`/admin/applications${params.toString() ? `?${params}` : ''}`);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [statsResult, appsResult] = await Promise.all([
        supabase.rpc('dashboard_stats'),
        supabase
          .from('admissions')
          .select('id, reference_number, student_first_name, student_last_name, grade, email, status, created_at')
          .order('created_at', { ascending: false })
          .limit(5)
      ]);
      if (statsResult.error) throw statsResult.error;
      if (appsResult.error) throw appsResult.error;
      setStats(statsResult.data);
      setRecentApplications(appsResult.data ?? []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('The dashboard could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading || error) {
    return (
      <AdminLayout>
        <div className="admin-dashboard">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading dashboard...</p>
            </div>
          ) : (
            <div className="error-banner" role="alert">
              {error}{' '}
              <button type="button" className="btn-retry" onClick={fetchDashboardData}>Try again</button>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  const summary = stats?.summary ?? { applications: {}, enquiries: {} };
  const apps = summary.applications ?? {};
  const enquiries = summary.enquiries ?? {};
  const attention = stats?.attention ?? {};
  const admissions = stats?.admissions ?? {};
  const statusBreakdown = stats?.statusBreakdown || [];
  const activity = stats?.recentActivity || [];
  const admissionState = ADMISSION_STATE[admissions.status] ?? ADMISSION_STATE.closed;

  // Only items with something to do are shown.
  const attentionItems = [
    { key: 'newApplications', text: (n) => `${plural(n, 'new application')} to review`, to: '/admin/applications?status=pending' },
    { key: 'requiringInformation', text: (n) => `${plural(n, 'application')} waiting for more information`, to: '/admin/applications?status=document-pending' },
    { key: 'newEnquiries', text: (n) => `${plural(n, 'new enquiry', 'new enquiries')} to answer`, to: '/admin/contacts?status=new' },
    { key: 'possibleDuplicates', text: (n) => `${plural(n, 'possible duplicate application')} to check`, to: '/admin/applications?duplicates=1' },
    { key: 'upcomingInterviews', text: (n) => `${plural(n, 'interview')} or test${n === 1 ? '' : 's'} in the next 7 days`, to: '/admin/applications?status=interview-scheduled' },
    { key: 'failedEmails', text: (n) => `${plural(n, 'email')} could not be delivered`, to: '/admin/notifications?status=failed', urgent: true }
  ].filter((item) => (attention[item.key] ?? 0) > 0);

  const cards = [
    { label: 'Total Applications', value: apps.total, icon: <FaClipboardList />, color: '#3498db', onClick: () => openFilteredApplications({}) },
    { label: 'New', value: apps.new, icon: <FaClock />, color: '#f39c12', onClick: () => openFilteredApplications({ status: 'pending' }) },
    { label: 'Under Review', value: apps.underReview, icon: <FaSearch />, color: '#9b59b6', onClick: () => openFilteredApplications({ status: 'reviewing' }) },
    { label: 'Accepted', value: apps.accepted, icon: <FaCheckCircle />, color: '#27ae60', onClick: () => openFilteredApplications({ status: 'approved' }) },
    { label: 'Requiring Information', value: apps.requiringInformation, icon: <FaFileAlt />, color: '#e67e22', onClick: () => openFilteredApplications({ status: 'document-pending' }) },
    { label: 'New Enquiries', value: enquiries.new, sub: `of ${enquiries.total ?? 0} total`, icon: <FaEnvelope />, color: '#1abc9c', onClick: () => navigate('/admin/contacts?status=new') }
  ];

  const describe = (e) => {
    switch (e.kind) {
      case 'application_submitted':
        return <>Application <Link to={`/admin/applications/${e.applicationId}`}>{e.reference}</Link> submitted for {e.detail}</>;
      case 'status_changed':
        return (
          <>
            <Link to={`/admin/applications/${e.applicationId}`}>{e.reference}</Link>: {statusLabel(e.from)} → {statusLabel(e.to)}
            {e.actor && <> by {e.actor}</>}
          </>
        );
      case 'enquiry_received':
        return <>New {ENQUIRY_TYPE[e.enquiryType] ?? 'enquiry'} from {e.detail}</>;
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back, {user?.full_name || user?.username}!</p>
        </div>
        <Link to="/admin/admission-controls" className={`admission-state ${admissionState.className}`} data-testid="admission-state">
          {admissionState.label}
          {admissions.academicYear ? ` · ${admissions.academicYear}` : ''}
        </Link>
      </div>

      {/* Attention required */}
      <section className="dashboard-section attention" aria-labelledby="attention-heading">
        <h2 id="attention-heading">Attention required</h2>
        {attentionItems.length === 0 ? (
          <p className="no-data">Nothing needs your attention right now.</p>
        ) : (
          <ul className="attention-list">
            {attentionItems.map((item) => (
              <li key={item.key} className={item.urgent ? 'urgent' : ''}>
                <Link to={item.to}>{item.text(attention[item.key])}</Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Statistics Cards */}
      <div className="stats-grid">
        {cards.map((card, i) => (
          <motion.button
            type="button"
            key={card.label}
            className="stat-card clickable"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i }}
            onClick={card.onClick}
          >
            <div className="stat-icon" style={{ background: card.color }} aria-hidden="true">
              {card.icon}
            </div>
            <div className="stat-content">
              <h3>{card.value ?? 0}</h3>
              <p>{card.label}</p>
              {card.sub && <small>{card.sub}</small>}
            </div>
          </motion.button>
        ))}
      </div>

      <div className="dashboard-columns">
        {/* Recent activity */}
        <section className="dashboard-section" aria-labelledby="activity-heading">
          <h2 id="activity-heading">Recent activity</h2>
          {activity.length === 0 ? (
            <p className="no-data">No recent activity.</p>
          ) : (
            <ul className="activity-list">
              {activity.map((e, i) => (
                <li key={`${e.kind}-${e.at}-${i}`}>
                  <span className="activity-text">{describe(e)}</span>
                  <time dateTime={e.at}>{new Date(e.at).toLocaleString()}</time>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Status Breakdown Chart */}
        <section className="dashboard-section" aria-labelledby="status-heading">
          <h2 id="status-heading">Applications by Status</h2>
          {statusBreakdown.length === 0 ? (
            <p className="no-data">No applications yet.</p>
          ) : (
            <div className="status-chart">
              {statusBreakdown.map((item) => (
                <button
                  type="button"
                  key={item.status}
                  className="status-bar-item"
                  onClick={() => openFilteredApplications({ status: item.status })}
                  title={`View ${statusLabel(item.status)} applications`}
                >
                  <div className="status-bar-label">
                    <span className="status-name">{statusLabel(item.status)}</span>
                    <span className="status-count">{item.count}</span>
                  </div>
                  <div className="status-bar-track">
                    <div
                      className="status-bar-fill"
                      style={{
                        width: `${stats.totalApplications ? (item.count / stats.totalApplications) * 100 : 0}%`,
                        background: statusColor(item.status)
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Recent Applications */}
      <section className="dashboard-section" aria-labelledby="recent-heading">
        <div className="section-header">
          <h2 id="recent-heading">Recent Applications</h2>
          <Link to="/admin/applications" className="btn-view-all">
            View All
          </Link>
        </div>

        {recentApplications.length === 0 ? (
          <p className="no-data">No applications yet.</p>
        ) : (
          <div className="applications-table-container">
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Student Name</th>
                  <th>Grade</th>
                  <th>Parent Email</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentApplications.map((app) => (
                  <tr key={app.id}>
                    <td>{app.reference_number}</td>
                    <td>{app.student_first_name} {app.student_last_name}</td>
                    <td>{gradeLabel(app.grade)}</td>
                    <td>{app.email}</td>
                    <td>
                      <span className="status-badge" style={{ background: statusColor(app.status) }}>
                        {statusLabel(app.status)}
                      </span>
                    </td>
                    <td>{new Date(app.created_at).toLocaleDateString()}</td>
                    <td>
                      <Link to={`/admin/applications/${app.id}`} className="btn-action">
                        <FaEye /> View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
